import { format, startOfDay } from "date-fns";
import { useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AddOnTextInput, Alert, Button, Icon, Typography } from "@/components/atoms";
import { extractAirportIata } from "@/helpers/bookingHelpers";
import { flightDisruptionReason, isSameFlightNumber } from "@/helpers/bookingHelpers";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  setPickupDatetime,
  setPickupFlightDelayMinutes,
  setPickupFlightLandingUsed,
} from "@/redux/slices/clientBooking/clientBookingSlice";
import {
  selectEditJobId,
  selectPickup,
  selectPickupDatetime,
  selectPickupFlightDelayMinutes,
  selectPickupFlightLandingUsed,
  selectPickupFlightDerivedArrival,
  selectPickupFlightDerivedArrivalSource,
  selectEditOriginal,
  selectPickupFlightChangedSinceCommit,
  selectPickupFlightFrozenReason,
  selectPickupFlightNumber,
} from "@/redux/slices/clientBooking/selectors";
import { clsx } from "@/utils";
import { useBookingViewOnly } from "./BookingViewOnlyContext";
import "./DateTimePanel.css";
import { FlightDetailsCard } from "./FlightDetailsCard";
import { FlightDetailsModal } from "./FlightDetailsModal";
import { ViewOnlyFieldset } from "./ViewOnlyFieldset";
import {
  LandingSource,
  flightDateFromPickup,
  flightLandingUsedIso,
  flightPickupCommitValue,
  flightWillArrive,
  landingTime,
  pickupFromFlight,
  utcIsoToAirportIso,
} from "./flightPickup";
import { useFlightCapture } from "./hooks/useFlightCapture";
import { useFlightDetails } from "./hooks/useFlightDetails";
import { useFlightTrackingEnabled } from "./hooks/useFlightTrackingEnabled";
import { STEP_MINUTES, defaultPickupSlot, validatePickup } from "./pickupTime";
import { useForceValidate } from "./useForceValidate";

type WhoLayout = "card" | "sheet";

interface DateTimePanelProps {
  /** "card" = desktop side panel; "sheet" = mobile bottom sheet (adds a Close action). */
  layout: WhoLayout;
  onClose: () => void;
  /**
   * "card" layout only: called after Proceed commits the pickup so the wizard opens the next Trip
   * Details sub-step (Vehicle & Transfer). The "sheet" layout closes back to the list instead.
   */
  onAdvance?: () => void;
  /** When true, run validation immediately (the user was routed here by a failed exit). The default
   * pickup slot is already valid, so this typically shows no error — the danger row is the signal. */
  forceValidate?: boolean;
}

const MERIDIEMS = ["AM", "PM"] as const;
type Meridiem = (typeof MERIDIEMS)[number];

/**
 * The backend's stored arrival columns, translated into the card's own vocabulary.
 *
 * Matched on the FAMILY rather than the exact key, for the same reason `appliedArrivalLabel` is: the
 * backend emits raw stored column names and a suffix change must not silently drop the kind. Two
 * tolerances for one column is how the client panel ends up unlabelled while the operator panel labels
 * the same booking correctly.
 *
 * A lookup rather than an object index, so a pathological value like `constructor` or `toString` cannot
 * return something off `Object.prototype` typed as a LandingSource. Anything unmatched is undefined
 * rather than guessed.
 *
 * The creation snapshot is the MAJORITY case, not an edge one — 34 of 46 seeded staging jobs have no
 * applied arrival and fall back to it. It maps to the runway `scheduled`, not `gateScheduled`: it comes
 * from Cirium Schedules with no tracked row, where every gate column is null, so claiming the gate family
 * would be false about where the value came from. Both label the row "Scheduled arrival", but the NOUN
 * differs — "scheduled arrival" against "scheduled gate arrival" — and the backend's own
 * `appliedArrivalNoun` says the former, which the two surfaces must not contradict on one booking.
 */
const STORED_ARRIVAL_SOURCES: [RegExp, LandingSource][] = [
  [/gate_actual/, "gateActual"],
  [/gate_estimated/, "gateEstimated"],
  [/gate_scheduled/, "gateScheduled"],
  [/at_booking|creation/, "scheduled"],
];

const toLandingSource = (source?: string | null): LandingSource | undefined =>
  (source && STORED_ARRIVAL_SOURCES.find(([pattern]) => pattern.test(source))?.[1]) || undefined;

const to12Hour = (h24: number) => String(((h24 + 11) % 12) + 1);
/** The inverse, for an hour already validated as 1–12. Shared so the touched-check and the branch that
 *  builds the pickup can never read the same fields as two different times. */
const to24Hour = (h12: number, meridiem: Meridiem) => (meridiem === "PM" ? (h12 % 12) + 12 : h12 % 12);
/** Strip to at most 2 digits and clamp to `max`, so an out-of-range time can't be typed in. */
const sanitize = (value: string, max: number) => {
  const digits = value.replace(/\D/g, "").slice(0, 2);
  return digits === "" ? "" : String(Math.min(parseInt(digits, 10), max));
};

/**
 * Centred 2-digit number field for hour / minute entry. Sized to Figma (80×90.5, 24px y-padding,
 * h2 text) and borrows TextInput's active treatment — border-primary + `shadow-input` glow on focus.
 */
const TimeField = ({
  value,
  onChange,
  onBlur,
  max,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  /** Largest value that can be typed (12 for hour, 59 for minute). */
  max: number;
  placeholder?: string;
  ariaLabel: string;
}) => (
  <input
    type="text"
    inputMode="numeric"
    value={value}
    placeholder={placeholder}
    aria-label={ariaLabel}
    onChange={(e) => onChange(sanitize(e.target.value, max))}
    onBlur={onBlur}
    className="h-20 min-w-0 flex-1 rounded-lg border border-neutral-mid-gray py-6 text-center text-2xl font-bold text-neutral-black focus:border-primary focus:shadow-input focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-neutral-surface-gray disabled:text-neutral-silver-gray max-lg:text-lg"
  />
);

/**
 * "Date and Time Pickup" — the Date/Time sub-panel of the Trip Details step. An inline calendar
 * (react-datepicker, themed via DateTimePanel.css) + an hour/minute/AM-PM entry. The selection is
 * committed to clientBookingSlice on Proceed (not live), matching the explicit button in Figma.
 */
export const DateTimePanel = ({ layout, onClose, onAdvance, forceValidate = false }: DateTimePanelProps) => {
  const viewOnly = useBookingViewOnly();
  // Edit mode relabels the commit CTA "Confirm" (it closes the sub-panel rather than advancing).
  const isEdit = useAppSelector(selectEditJobId) != null;
  const dispatch = useAppDispatch();
  const committed = useAppSelector(selectPickupDatetime);
  // An unparseable committed value is treated as no pickup at all. date-fns throws on an Invalid Date,
  // and this seeds both the calendar and the flight lookup's date, so an unguarded parse took the whole
  // panel down rather than degrading to manual entry.
  const committedDate = useMemo(() => {
    if (!committed) return null;
    const parsed = new Date(committed);
    return isNaN(parsed.getTime()) ? null : parsed;
  }, [committed]);
  // Seed from the committed pickup, else default to the next valid slot (now + 15 min on a 5-min grid).
  const seed = useMemo(() => committedDate ?? defaultPickupSlot(), [committedDate]);

  // The FLIGHT's date — the day to ask Cirium about, which is NOT the pickup's day. A late landing plus a
  // wait crosses midnight (23:45 + 30 → 00:15 the next day), so reading the lookup date off the derived
  // pickup asks about a day the flight never flew: it stops resolving, the card claims it cannot find a
  // perfectly good flight, and no landing is sent for precisely the late-arrival bookings the provenance
  // exists for.
  //
  // Recovered by subtracting the booked wait from the booked pickup, which is the airport-local wall clock
  // of the arrival the pickup was built on. That is the same date `flight_arrival_scheduled_at_booking`
  // gives when converted to the arrival airport's zone — it IS that arrival, read through the pickup
  // instead of through a timezone the client does not have here. The stored arrival reaches the client as
  // `flight_pickup_derived_arrival`, but in UTC, and the arrival airport's zone only arrives WITH the
  // lookup this date is needed to make; `pickup_time` carries the offset, but `viewDraft` normalises it to
  // a naive wall clock before the panel sees it. So the two agree wherever the stored arrival is the
  // pickup's actual operand, and the pickup is the only one of the pair reachable from here.
  const pickupFlightNumber = useAppSelector(selectPickupFlightNumber);
  // Seeded from the committed delay so replaying an existing flight booking shows the delay the
  // client actually booked; without it the panel re-derived `pickup = landing + 0` on Confirm and
  // silently clobbered the booked time (DEV-1429). Committed on Proceed, like the date/time fields.
  const committedDelay = useAppSelector(selectPickupFlightDelayMinutes);
  const committedFlightDate = useMemo(
    () => (committed && pickupFlightNumber ? flightDateFromPickup(committed, committedDelay) : null),
    [committed, pickupFlightNumber, committedDelay]
  );

  // Both fields start pre-filled with a valid slot, so Proceed is actionable the moment the panel opens.
  // The calendar starts on the flight's date when there is one — in flight mode it IS the flight-date
  // picker, and showing the pickup's date would put a different day above the flight card's own legs.
  const [date, setDate] = useState<Date | null>(() => {
    if (!committedFlightDate) return seed;
    // Parsed as a LOCAL midnight, matching how `format(date, "yyyy-MM-dd")` reads it back out.
    const parsed = new Date(`${committedFlightDate}T00:00:00`);
    return isNaN(parsed.getTime()) ? seed : parsed;
  });
  const [hour, setHour] = useState(() => to12Hour(seed.getHours()));
  const [minute, setMinute] = useState(() => String(seed.getMinutes()).padStart(2, "0"));
  const [meridiem, setMeridiem] = useState<Meridiem>(() => (seed.getHours() >= 12 ? "PM" : "AM"));
  const [error, setError] = useState<string | null>(null);
  // The disrupted panel's way out — the same modal the Address step opens, resolved through the same hook
  // so the two call sites cannot drift on when a committed pickup is thrown away.
  const [flightModalOpen, setFlightModalOpen] = useState(false);
  const { confirmFlightNumber, notFlying } = useFlightCapture();

  // Airport pickup with a captured flight number: resolve the flight for the selected date and,
  // when it lands on a known time, derive the pickup from landing + a driver-arrival delay
  // instead of manual time entry. Re-resolves automatically whenever the calendar date changes.
  // The flight-tracking add-on. Gated on `enabled` alone, never `enabled || isResolving`: the lookup is
  // the expense being gated, and an operator that may not have the add-on must not pay for one. The hook
  // carries the reasoning, including why unknown reads as OFF and what makes that safe.
  const { enabled: flightTrackingEnabled, isResolving: isFlightTrackingResolving } = useFlightTrackingEnabled();

  const pickup = useAppSelector(selectPickup);
  // The landing the COMMITTED pickup was derived from, so the anchored branch can hand the booked triple
  // back untouched instead of re-deriving two thirds of it.
  const committedLandingUsed = useAppSelector(selectPickupFlightLandingUsed);
  const [delayMinutes, setDelayMinutes] = useState(() => String(committedDelay ?? 0));
  // Empty or unparseable reads as 0 — the field's own default, and what the blur handler restores.
  const delayMin = useMemo(() => {
    const parsed = parseInt(delayMinutes, 10);
    return isNaN(parsed) ? 0 : parsed;
  }, [delayMinutes]);
  // Compared against the committed FLIGHT date in flight mode, not the pickup's. The calendar now holds the
  // flight's date, and a pickup pushed past midnight by the wait sits on the following day — so comparing
  // the two would read an untouched late-arrival booking as date-edited, re-deriving its pickup off the
  // live flight on every open and defeating the anchoring DEV-1499 exists to hold.
  const dateTouched =
    committedFlightDate && date
      ? format(date, "yyyy-MM-dd") !== committedFlightDate
      : !committedDate || !date || format(date, "yyyy-MM-dd") !== format(committedDate, "yyyy-MM-dd");

  // Hour, minute and meridiem against the COMMITTED pickup's own time. `dateTouched` cannot carry this:
  // it compares `yyyy-MM-dd` and nothing else, by design. Without a time term a client who changed ONLY the
  // time left every operand reading untouched — the anchored branch in `resolvePickup` fired, handed the
  // booked pickup straight back, and the typed value was dropped with no error shown (DEV-1507). Not a
  // flight-only defect: that branch is not gated on a flight, so an ordinary booking hits it too.
  //
  // Equality with the SEED has to read as UNTOUCHED. The fields are seeded from the committed pickup, so a
  // term that fired on open would re-derive on every open and defeat exactly the anchoring DEV-1499 exists
  // to hold — silently moving an unedited booking to wherever the flight has since drifted. Hence the
  // comparison is against `committedDate`, which IS what `seed` reads when there is one, and against the
  // parsed 24-hour pair rather than the raw strings, so an "09" typed over a seeded "9" is not an edit.
  //
  // The RAW minute, never the grid-snapped one: a booked pickup can sit off the 5-minute grid — clamped to
  // MIN_LEAD_MINUTES by the engine, or hand-edited by an operator — and seeds an off-grid minute that
  // snapping before comparing would read as edited the moment the panel opened.
  //
  // An unparseable field reads as TOUCHED, which is the safe direction: it falls through to the manual
  // branch and the client is told "Enter a valid hour (1–12)", rather than a half-typed time silently
  // committing the booked one. With no committed pickup the term is moot — `!committedDate` already opens
  // `rederive` on the create path, which this defect never reached.
  const typedHour = parseInt(hour, 10);
  const typedMinute = parseInt(minute, 10);
  const typedTimeIsValid = !isNaN(typedHour) && typedHour >= 1 && typedHour <= 12 && !isNaN(typedMinute);
  const timeTouched =
    !!committedDate &&
    (!typedTimeIsValid || to24Hour(typedHour, meridiem) !== committedDate.getHours() || typedMinute !== committedDate.getMinutes());

  // Stored, so it needs no flight lookup — which is the whole point. The lookup is skipped in view-only
  // to stop RECOMPUTATION, and that skip must not take disclosure with it: a closed booking on a
  // cancelled flight still has to say so, and most bookings with a disruption are closed.
  //
  // A different DATE is a different flight instance, so the reason no longer describes what is on screen.
  // A changed wait is not: the flight is the same one and a cancellation is still true. A changed flight
  // NUMBER clears it at the source, in the slice.
  const frozenReason = useAppSelector(selectPickupFlightFrozenReason);
  const storedDisruption = flightDisruptionReason(dateTouched ? null : frozenReason);

  const flightDate = date ? format(date, "yyyy-MM-dd") : null;
  // Scope-aware: authed → account-scoped client lookup, guest → provider-scoped public twin. Same
  // FlightDetails contract either way; the inactive scope's query is skipped inside the hook.
  const {
    data: flight,
    isFetching: isFlightFetching,
    isError: isFlightError,
  } = useFlightDetails({
    flightNumber: pickupFlightNumber ?? "",
    date: flightDate ?? "",
    airport: extractAirportIata(pickup) ?? undefined,
    // NO view-only skip (DEV-1510). A non-editable booking used to be skipped here unless it carried a
    // stored disruption, which left a reader of a perfectly ordinary flight booking looking at a bare
    // calendar: no card, no flight number, no delay. Staging J000472 (QF2) showed only the calendar.
    //
    // The skip was justified by "a closed booking must never render a live-derived WORKING" — the pickup is
    // frozen while the flight keeps moving, so a recomputed one fails to add up, and two people opening the
    // same job on different days see different arithmetic for the same unchanged time. That rule still
    // holds, and is still worth every word of this comment. What changed is WHERE it is enforced: at
    // `rederive`, which is false for a reader, so the card renders the FROZEN arrival and the anchored
    // branch hands the booked pickup straight back. Enforcing it by withholding the data instead also
    // withheld the disclosure, and the two are not the same thing.
    //
    // Established behaviour, not new risk: the disrupted path has fetched on non-editable bookings since
    // DEV-1509 — it leads with the card, and a warning with no card is an assertion with nothing behind it.
    // This is the ordinary case being made consistent with it.
    //
    // The flight-tracking add-on is NOT repeated here: `useFlightDetails` gates on it itself, so there is
    // one rule protecting the request rather than one per caller. `flightTrackingEnabled` below is still
    // this panel's, for what it RENDERS and for holding Proceed through the guest resolve window.
    skip: !pickupFlightNumber || !flightDate,
  });
  // A pickup is only flight-derivable when the flight will actually arrive as looked up. Shared with the
  // sheet-level re-derivation so the same booking cannot derive on one surface and not the other.
  const landing = !isFlightFetching && !isFlightError && flightWillArrive(flight) ? landingTime(flight) : null;
  /** True when the pickup is being derived from the flight rather than typed in manually. */
  const isFlightDerived = !!(pickupFlightNumber && landing);

  // The flight-derived pickup is ANCHORED to what was booked, not recomputed from the live arrival on
  // every Confirm. Otherwise confirming an existing booking unchanged moves the pickup to wherever the
  // flight has drifted — and since the server will not re-snapshot an unchanged flight, the frozen
  // arrival stays put while the pickup moves, leaving `derived_arrival + wait ≠ pickup_time`.
  // Re-deriving against a moved flight is a deliberate act with its own confirmation (D5), not a side
  // effect of confirming an unrelated edit.
  //
  // So we re-derive only from operands the client controls here: the wait, the date, or the flight
  // itself (a flight change clears the committed pickup in LocationPanel, landing us in `!committed`).
  // Compare the parsed minutes, not the raw strings — the input permits leading zeros, and "045"
  // against a committed 45 is the same wait, not an edit.
  // A NEW FLIGHT is an operand too, and was missing. Without it, changing QF2 to QF8 and confirming
  // committed the old pickup — derived from QF2's arrival — while the card above showed QF8's legs.
  // `anchoredWorkingHolds` did not catch it either: the stored arrival was still QF2's, so the working
  // appeared to add up against a flight that was no longer selected.
  //
  // Gated on a RESOLVED landing for the new flight: a freshly typed number is briefly present with no
  // lookup settled, and flipping then would rebuild the pickup off no arrival at all.
  //
  // Two baselines, because neither covers both paths. `editOriginal` is the booking as loaded, so it catches
  // a flight changed at any point in an edit session — but it does not exist on CREATE. The slice flag is
  // set by the change itself, so it covers create, and it is what replaced nulling `pickupDatetime` for this
  // job (DEV-1510) — that nulling forced the re-derivation by destroying the committed pickup, and took the
  // booked date with it.
  const originalFlightNumber = useAppSelector(selectEditOriginal)?.pickupFlightNumber ?? null;
  const flightChangedSinceCommit = useAppSelector(selectPickupFlightChangedSinceCommit);
  const flightTouched =
    !!landing && (flightChangedSinceCommit || (!!originalFlightNumber && !isSameFlightNumber(pickupFlightNumber, originalFlightNumber)));
  const delayTouched = delayMin !== (committedDelay ?? 0);
  // The disruption as ONE fact, whichever signal carried it: the booking's stored freeze reason first,
  // else the live lookup's status. They normally agree, and the stored one wins because it is the reason
  // the engine actually acted on.
  //
  // PRESENTATION ONLY. Everything below that decides a pickup reads `storedDisruption`, unchanged — a live
  // cancelled or diverted status already yields no landing to derive from, so folding it in here cannot
  // move a derivation.
  const liveDisruption = flight?.status === "cancelled" || flight?.status === "diverted" ? flight.status : null;
  const disruptedAs = storedDisruption ?? liveDisruption;
  const isDisrupted = !!pickupFlightNumber && !!disruptedAs;

  // Live GATE landing against the one the booked pickup was built on — both gate, so they are the same
  // quantity measured at two times. (The card's leg row is the RUNWAY chain and is a different event
  // entirely; it needs nothing from this.)
  //
  // The stored value is UTC while every other flight time here carries the airport's own offset, so it is
  // converted through the arrival airport's timezone rather than read as a wall clock.
  const derivedArrival = useAppSelector(selectPickupFlightDerivedArrival);
  const storedLandingIso = useMemo(() => {
    const zone = flight?.arrival?.airport?.timezone;
    return derivedArrival && zone ? utcIsoToAirportIso(derivedArrival, zone) : null;
  }, [derivedArrival, flight?.arrival?.airport?.timezone]);
  // Requiring a STORED arrival makes drift an edit-path question by construction:
  // `pickupFlightDerivedArrival` is written in exactly one place — `mapBookingDetailToViewDraft` —
  // `rebookDraft` nulls the flight fields outright, and `clientBooking` is not in the persist whitelist, so
  // no create-path draft can carry one. On create there is simply the live flight and the pickup it
  // produces, which needs no comparison.
  const liveLanding = !storedDisruption && landing ? landing : null;
  const driftedAtAll = !!liveLanding && !!storedLandingIso && Date.parse(liveLanding) !== Date.parse(storedLandingIso);
  // Drift is an operand too, and ANY drift re-derives — there is NO materiality threshold on this path
  // (DEV-1531, superseding D4/D5 on DEV-1490 for the edit path). The 15/30 rule is a churn control for the
  // ENGINE, which wakes repeatedly against a flight that moves all day; an edit is one deliberate human
  // save, so the churn it prevents does not exist here. `flightUpdateIsMaterial` stays in `flightPickup.ts`
  // for the engine's own rule — only this caller drops it. The pickup therefore re-derives from the arrival
  // the card is already showing, so the two can never disagree at any size of drift, and a pickup that
  // MOVED is disclosed on the change-review diff before the client confirms.
  //
  // Still gated on the re-derived pickup being COMMITTABLE, which is not pedantry and is NOT the threshold:
  // drift is the one operand the client cannot move. A flight that landed early enough puts landing + wait
  // in the past, and re-deriving onto it would hand the client "Pickup time can't be in the past" on an edit
  // they did not make, with no lever on this panel to clear it — `resolvePickup`'s anchor branch skips
  // validation precisely to stop an elapsed pickup blocking an unrelated edit. Declining to re-derive keeps
  // that escape open AND keeps the pair together: the card goes on showing the frozen landing, which is
  // still the one the held pickup was built on.
  const driftRederives = driftedAtAll && !validatePickup(pickupFromFlight(liveLanding!, delayMin));

  // NEVER for a reader (DEV-1510). Every operand above is one the client moved — except `driftRederives`,
  // which is computed purely from the lookup, and the lookup now runs on non-editable bookings too. Left
  // ungated, a closed booking whose flight has since drifted would flip `rederive` on its own, null
  // `storedArrival`, and put the LIVE arrival on the card beside a pickup that was built from the frozen one
  // — the exact "live-derived working on a closed booking" the skip above used to prevent. Nothing can write
  // it back (there is no Proceed in view-only, and `useSheetFlightPickup` gates itself on viewOnly), so this
  // is about what is DISPLAYED, which is where the guarantee actually lives now.
  const rederive = !viewOnly && (!committedDate || delayTouched || dateTouched || timeTouched || flightTouched || driftRederives);
  // The anchored branch's own condition, hoisted: `handleProceed` needs it too, so the visible-minute
  // snap does not fire on a pickup that branch handed back untouched. See there for why that matters.
  const isAnchored = !rederive && !!committed;

  // D14: the arrival on screen must be the one the pickup on screen was derived from, and the pair moves
  // together. While the pickup is the booked value we show the arrival it was actually built from — the
  // stored one, frozen; the moment the pickup re-derives — an operand the client controls moved, or the
  // flight itself drifted materially — the arrival shown becomes that same live value.
  //
  // Which is the whole disclosure story on this card: the pickup is never derived from a landing other than
  // the one beside it, so the card has no discrepancy to explain. A pickup that MOVED is disclosed on the
  // change-review diff instead, stated as a before → after and gating the save.
  const storedArrival = rederive ? null : storedLandingIso;

  // D11a is unconditional: a working line reconciles exactly against the pickup beside it, or it is not
  // rendered at all. On the re-derived branch it reconciles by construction — same operands, same call.
  // On the ANCHORED branch it only holds if the stored arrival and the stored pickup actually agree, and
  // they can disagree today: PickupTimeCalculator clamps a pickup to MIN_LEAD_MINUTES when a callback
  // lands moments before it, an operator can hand-edit the pickup, and the wait can change after the
  // last engine write. D14 asserting the pair reconciles is design intent, not a runtime guarantee, and
  // this is the surface a paying customer reads — so check it rather than assume it.
  //
  // Compared as rendered wall clocks, which is what the reader checks. A booking with no stored arrival
  // cannot reconcile at all: its pickup is the booked value while the card shows a live arrival, so it
  // shows no working either.
  const wallClock = (d: Date) => format(d, "yyyy-MM-dd HH:mm");
  const anchoredWorkingHolds =
    !!storedArrival && !!committedDate && wallClock(flightPickupCommitValue(storedArrival, delayMin)) === wallClock(committedDate);

  // Passed to the card as `{ iso, source }`. `source` tells the card whether its own runway row names a
  // different event from the one in the footer. The stored source arrives as the backend's raw column name,
  // so it is translated here — the card's vocabulary is LandingSource and it should not learn a second one.
  //
  // An unrecognised or absent value maps to undefined rather than to a guess: not knowing which kind of
  // arrival the pickup was built on is the honest answer, and every consumer already handles it.
  const derivedArrivalSource = useAppSelector(selectPickupFlightDerivedArrivalSource);
  const frozenSource = toLandingSource(derivedArrivalSource);
  const frozenArrival = anchoredWorkingHolds && storedArrival ? { iso: storedArrival, source: frozenSource } : null;

  // The edit path carries NO threshold, on the backend or here: the pickup rebuilds from the live gate
  // landing by the full drift however small.
  //
  // Still gated on `delayTouched`, which since DEV-1531 no longer tracks when the pickup moves: a drifted
  // booking has ALREADY re-derived before anyone touches the wait, so this names the new time only if they
  // happen to. The primary disclosure is the change-review diff on Update Booking, which fires either way;
  // this button label is the secondary one and now under-reports. Widening it to `driftRederives` is a
  // separate call — see DEV-1531.
  const editedDriverArrival = driftedAtAll && delayTouched && liveLanding ? flightPickupCommitValue(liveLanding, delayMin) : null;

  // Why the pickup is being typed rather than derived — one sentence, one fact, ordered so the fact we
  // hold with most certainty wins. A cancelled flight is usually pulled from the schedule, so the lookup
  // that fails is often failing BECAUSE of the disruption; leading with "we couldn't find it" would report
  // the symptom and drop the cause.
  //
  // View-only drops the call to action. Its fields are a read-only replay of the booked time, so telling
  // that reader to enter one points at a control the closed booking will not let them use.
  const noticeSuffix = viewOnly ? "." : " — enter your pickup time instead.";
  const flightNotice = disruptedAs
    ? // No call to action on a DISRUPTION: that state has no time fields, so "enter your pickup time
      // instead" would point at a control that is not there. The two things a passenger can actually do —
      // change the flight, or say they are not flying — are behind the trigger below the alert.
      `Flight ${pickupFlightNumber} is ${disruptedAs}.`
    : isFlightError
    ? `The flight lookup is unavailable right now${noticeSuffix}`
    : flight && flight.status !== "not_found"
    ? `Flight ${pickupFlightNumber} is ${flight.status}${noticeSuffix}`
    : `We couldn't find flight ${pickupFlightNumber} on that date${noticeSuffix}`;

  /** Normalise the hour to 1–12 when the field loses focus (empty / 0 → 12). */
  const snapHour = () => {
    const h = parseInt(hour, 10);
    setHour(isNaN(h) || h < 1 ? "12" : String(Math.min(h, 12)));
  };

  /** Round the typed minute onto the 5-min grid when the field loses focus. */
  const snapMinute = () => {
    // Not while the pickup is anchored. A booked pickup can sit off the grid — clamped to MIN_LEAD_MINUTES,
    // or hand-edited — so rounding its seeded minute would rewrite a field nobody typed in, and
    // `timeTouched` would read that as an edit: the anchor releases on a focus and a blur, and the booked
    // pickup, its wait and its landing are replaced by a value the client never entered.
    if (isAnchored) return;
    if (minute === "") return setMinute("00");
    const m = parseInt(minute, 10);
    if (isNaN(m)) return;
    setMinute(String(Math.min(55, Math.round(m / STEP_MINUTES) * STEP_MINUTES)).padStart(2, "0"));
  };

  // Resolve the committable pickup ISO from the current inputs, or an error message. No side effects,
  // so both Proceed (which commits) and validation display (which only shows the error) can share it.
  //
  // The pickup, the wait and the landing travel TOGETHER rather than being recomputed by the caller,
  // because the server verifies them as one claim: `landing + delay*60 === pickup`, to the second. Each
  // branch therefore states all three, and only the branch that actually builds a pickup out of a landing
  // reports one — reading `landing` again at the call site would attach the LIVE arrival to a pickup taken
  // from somewhere else, which is precisely the pair the server refuses.
  const resolvePickup = (): { error: string } | { value: string; delayMinutes: number | null; landingUsed: string | null } => {
    if (!date) return { error: "Please select a date" };

    // Nothing the client controls has moved: commit the booked pickup back unchanged. Deliberately skips
    // validatePickup — re-validating an already-booked pickup would block an unrelated edit on a booking
    // whose lead time has since elapsed.
    //
    // Keyed on the ANCHOR, not on the flight lookup. With tracking off there is no lookup and so no
    // landing, which used to drop an untouched flight booking into the manual branch below — where
    // validatePickup rejects a pickup that has since elapsed and the edit could not be saved at all.
    // The booked pickup handed back unchanged — so its companions are handed back unchanged too. Clearing
    // either of them here is not a no-op: re-opening this row and pressing Proceed again lands on this
    // branch, and dropping the wait leaves the server holding `pickup != arrival + wait` on a job the
    // client only meant to look at, while dropping the landing quietly reverts the booking to letting the
    // server resolve an arrival of its own. `committedDelay` rather than `delayMin` keeps a never-asked
    // null distinct from a deliberate 0.
    if (isAnchored && committed) return { value: committed, delayMinutes: committedDelay, landingUsed: committedLandingUsed };

    // Flight-derived pickup: landing time + "delay driver arrival by" minutes. The landing carries
    // its own date (an overnight flight can land the day after the selected flight date).
    if (pickupFlightNumber && landing) {
      // Validate the true landing+delay instant against now; commit the airport-local wall clock
      // (re-encoded browser-local) so the booking payload submits the pickup in the airport's
      // timezone, not the booker's browser timezone.
      const pickupError = validatePickup(pickupFromFlight(landing, delayMin));
      if (pickupError) return { error: pickupError };
      // The one branch with a landing to report — and it is the same `landing` the committed value is
      // built from on the next line, so the triple reconciles by construction rather than by agreement.
      return {
        value: flightPickupCommitValue(landing, delayMin).toISOString(),
        delayMinutes: delayMin,
        // Built from the landing THIS lookup resolved, on the day `flightDate` asked about — so the day
        // looked up and the landing sent can never describe different flights.
        landingUsed: flightLandingUsedIso(landing),
      };
    }

    const h = parseInt(hour, 10);
    const rawM = minute === "" ? 0 : parseInt(minute, 10);
    if (isNaN(h) || h < 1 || h > 12) return { error: "Enter a valid hour (1–12)" };
    if (isNaN(rawM) || rawM < 0 || rawM > 59) return { error: "Enter a valid minute (0–59)" };

    // Snap onto the 5-min grid the backend expects.
    const m = Math.min(55, Math.round(rawM / STEP_MINUTES) * STEP_MINUTES);
    const hour24 = to24Hour(h, meridiem);
    // In flight mode the calendar holds the FLIGHT's date, which is not the pickup's whenever the booked
    // wait pushed the pickup past midnight (23:45 + 30 → 00:15 the next day). Manual entry renders over
    // precisely those bookings — tracking off, disrupted, lookup failed — so building the typed time on the
    // calendar's date would move the booking back onto the flight's day, a change the client never asked
    // for. An untouched calendar therefore keeps the pickup's own date; one the client actually moved is
    // what they chose, and wins.
    const pickup = new Date(!dateTouched && committedDate ? committedDate : date);
    pickup.setHours(hour24, m, 0, 0);

    const pickupError = validatePickup(pickup);
    if (pickupError) return { error: pickupError };
    // A typed time is not derived from a flight, so it carries no wait, landing or flight date.
    return { value: pickup.toISOString(), delayMinutes: null, landingUsed: null };
  };

  // Show any validation error without committing/advancing — used when a failed "Next Step" routes
  // the user here. The pre-filled default slot is valid, so this usually clears the error.
  const validate = () => {
    const result = resolvePickup();
    setError("error" in result ? result.error : null);
  };
  useForceValidate(forceValidate, validate);

  const handleProceed = () => {
    const result = resolvePickup();
    if ("error" in result) return setError(result.error);
    setError(null);

    // Snap the visible minute field to the committed grid value (manual path only) so the UI agrees.
    //
    // Never on the ANCHORED path, which committed a pickup it did not build out of these fields: an
    // off-grid booked minute (clamped, or hand-edited) would be snapped to a value the booking does not
    // hold, and `timeTouched` would then read that snap as an edit the client never made — releasing the
    // anchor on the next Confirm and re-validating an already-booked pickup, which is the elapsed-lead-time
    // block the branch exists to prevent.
    if (!isFlightDerived && !isAnchored) {
      const rawM = minute === "" ? 0 : parseInt(minute, 10);
      const m = Math.min(55, Math.round(rawM / STEP_MINUTES) * STEP_MINUTES);
      if (m !== rawM) setMinute(String(m).padStart(2, "0"));
    }

    // The three the server checks as one claim, committed together and only ever from the same branch —
    // the wait goes on the payload so the server can keep `pickup = flight arrival + wait` true as the
    // flight moves (DEV-1429), and the landing is what lets it verify the pair it was given rather than
    // resolving a second arrival of its own. An absent landing is always a safe answer; a wrong one is
    // refused, and on edit clears a previously-good applied arrival.
    dispatch(setPickupDatetime(result.value));
    dispatch(setPickupFlightDelayMinutes(result.delayMinutes));
    dispatch(setPickupFlightLandingUsed(result.landingUsed));
    if (layout === "sheet") onClose();
    else onAdvance?.();
  };

  /**
   * A cancelled or diverted flight is a DECISION point, not an input problem. Three things: the card with
   * the status where the arrival line sits, the warning, and a way to change the flight. No wait input, no
   * time fields, no second red row.
   *
   * Rendered below the calendar, alongside the non-disrupted branch — a variable rather than an inline
   * branch only so the two arms read as siblings at the call site. The calendar is KEPT rather than
   * collapsed: rebooked onto the same flight number a day later is a real journey, and re-dating is the
   * only exit that serves it (a same-number re-confirm clears nothing). On a short viewport the card, the
   * warning and the trigger therefore sit below the fold; that is accepted — the calendar is the panel's
   * primary control and demoting it to surface the disruption changed what the panel is.
   *
   * The wait describes a derivation that is not happening — the pickup cannot come off a flight that is not
   * arriving — and the card plus the warning already carry the disruption, so a separate red row was two
   * messages for one fact.
   *
   * The TIME FIELDS go because both real outcomes already have a home, and neither is a hand-typed time on
   * a booking still labelled with a dead flight: a new flight (change it, and the normal derived flow
   * returns by itself — the slice clears the stored reason on the number change), or not flying (the
   * modal's "Not Flying" makes it an ordinary booking, where manual entry appears through the non-airport
   * path). A third overlapping route produces the least correct data of the three. Accepted trade: someone
   * who still wants the car at the booked time goes via "Not Flying" — one extra step, and semantically
   * honest, because that booking is no longer a flight booking.
   *
   * NOT-FOUND and LOOKUP-ERROR keep their fields, below. Those are not disruptions; we simply cannot
   * confirm anything, and removing the fields there would block booking outright. View-only keeps them too,
   * for the reason below — a reader is not booking, so nothing overlaps.
   *
   * DELIBERATELY NOT gated on the add-on, unlike the block below. The stored freeze reason is a fact the
   * booking already carries, so it reaches a closed booking and an operator who has turned tracking off
   * since. Gating it would hide a diverted flight from a passenger because of a billing setting — the card
   * is the part that needs the lookup, so with tracking off the warning still lands and only the evidence
   * is missing.
   *
   * View-only gets the card, the status, the calendar and the time fields — every one of them inert, none
   * of them hidden (DEV-1510). It gets nothing to PRESS, which is a different thing: there is no working on
   * this state to disagree with the frozen pickup, so disclosure costs that reader nothing, while removing
   * the fields left a reader of a closed booking unable to see the time it was actually booked for. The
   * disabling is `ViewOnlyFieldset`'s, already wrapping this panel — no separate read-only treatment.
   */
  const disruptedFlight = (
    <div className="mt-6 flex flex-col gap-3">
      {/* `not_found` gets NO card. A cancelled flight is usually pulled from the schedule, so the lookup
          can come back empty while the booking still carries the disruption — and the card rendered off
          that is the flight number twice over an em-dash for each leg, which is worse evidence for the
          warning than no card at all. */}
      {flight && flight.status !== "not_found" && <FlightDetailsCard flight={flight} disruptionStatus={disruptedAs} />}
      <Alert type="warning" message={flightNotice} />
      {!viewOnly && (
        /* Not a new control — the Airplane-icon text button from the Address step's flight row
           (LocationPanel), pointed at the same modal, which already handles both exits a disruption has:
           enter a new number, or Not Flying. */
        <button
          type="button"
          onClick={() => setFlightModalOpen(true)}
          className="flex items-center gap-2 self-start text-neutral-dark-gray hover:text-primary"
        >
          <Icon name="Airplane" size="sm" variant="Linear" className="shrink-0" />
          <Typography variant="muted">Change flight</Typography>
        </button>
      )}
    </div>
  );

  const manualTimeEntry = (
    <div className="mt-6">
      <Typography variant="action" className="block text-neutral-dark-gray">
        Enter time
      </Typography>
      {/* Uniform 9px between hour · colon · minute · AM/PM. */}
      <div className="mt-3 flex items-center gap-[9px]">
        <TimeField value={hour} onChange={setHour} onBlur={snapHour} max={12} ariaLabel="Hour" />
        <Typography variant="h2" className="text-neutral-dark-gray">
          :
        </Typography>
        <TimeField value={minute} onChange={setMinute} onBlur={snapMinute} max={59} ariaLabel="Minute" />
        {/* Stacked AM/PM — matches the fields' 80px height; radius only on the outer corners,
            a single mid-gray divider between the two. */}
        <div className="flex h-20 flex-col">
          {MERIDIEMS.map((m, i) => (
            <button
              key={m}
              type="button"
              onClick={() => setMeridiem(m)}
              className={clsx(
                "flex-1 px-4 text-lg font-semibold",
                i === 0 ? "rounded-t-lg" : "rounded-b-lg border-t border-neutral-mid-gray",
                meridiem === m
                  ? viewOnly
                    ? "bg-neutral-mid-gray text-neutral-silver-gray"
                    : "bg-primary-light text-primary"
                  : "bg-neutral-surface-gray text-neutral-dark-gray"
              )}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ViewOnlyFieldset>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5">
          <Typography variant="h2">Date and Time Pickup</Typography>
          <Typography variant="muted" className="mt-1 block text-neutral-dark-gray">
            Select a date and time for pickup
          </Typography>

          {/* Full-bleed: cancel the body's px-5 so the calendar spans the panel's inner width. View-only:
            day cells are <div>s (not form controls), so the fieldset can't disable them — block clicks
            with pointer-events-none and mute the selected day via the --view-only CSS. */}
          <div className={clsx("booking-calendar -mx-5 mt-6", viewOnly && "booking-calendar--view-only pointer-events-none")}>
            <DatePicker
              inline
              selected={date}
              onChange={(next) => setDate(next)}
              minDate={startOfDay(new Date())}
              calendarStartDay={1}
              formatWeekDay={(weekday) => weekday.slice(0, 2)}
              renderCustomHeader={({ monthDate, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                    className="p-1 text-neutral-dark-gray disabled:text-neutral-silver-gray"
                  >
                    <Icon name="ArrowLeft2" size="md" />
                  </button>
                  <Typography variant="action" className="text-neutral-black">
                    {monthDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                  </Typography>
                  <button
                    type="button"
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                    className="p-1 text-neutral-dark-gray disabled:text-neutral-silver-gray"
                  >
                    <Icon name="ArrowRight2" size="md" />
                  </button>
                </div>
              )}
            />
          </div>

          {/* The calendar is the panel's primary control and stays first, so the order is the same in both
            states. See `disruptedFlight` for why the calendar survives a disruption at all. */}
          {isDisrupted && disruptedFlight}

          {/* A closed booking's booked TIME, disabled by the fieldset like every other view-only field.
            Working panels still get no time fields on a disruption — a reader is not choosing a pickup,
            so the reason those fields overlap the two real exits does not apply. */}
          {isDisrupted && viewOnly && manualTimeEntry}

          {!isDisrupted &&
            (pickupFlightNumber && flightTrackingEnabled ? (
              <div className="mt-6 flex flex-col gap-3">
                {isFlightFetching ? (
                  <Typography variant="muted" className="text-neutral-dark-gray">
                    Finding flight {pickupFlightNumber}…
                  </Typography>
                ) : flight && landing ? (
                  <>
                    <FlightDetailsCard flight={flight} frozenArrival={frozenArrival} />
                    <Alert
                      type="info"
                      noIcon
                      className="border border-info"
                      message={
                        <span className="flex flex-col gap-1 text-left">
                          <Typography variant="action">Note</Typography>
                          <Typography variant="small-regular" className="font-medium">
                            Free waiting time starts when your driver arrives. By default, your driver will arrive at your flight&apos;s
                            landing time unless you choose to delay their arrival.
                          </Typography>
                        </span>
                      }
                    />
                    {/* Figma 4088:19974 — label, a full-width input with a "minutes" add-on divided by a
                    border, then the courtesy-waiting hint. Default 0 per the design annotation. */}
                    <div className="flex flex-col gap-1">
                      <label htmlFor="flight-delay-minutes">
                        <Typography variant="action" className="block text-neutral-dark-gray">
                          Delay driver arrival by
                        </Typography>
                      </label>
                      <AddOnTextInput
                        id="flight-delay-minutes"
                        aria-label="Delay driver arrival by"
                        inputMode="numeric"
                        endAddOn="minutes"
                        // AddOnTextInput wraps its input in a headlessui <Field> div that doesn't grow,
                        // so w-full alone leaves the input content-width; grow the Field so it fills.
                        className="w-full [&>div]:flex-1"
                        // The COMMITTED delay for a reader, never the input state. They agree today —
                        // the state is seeded from `committedDelay` and a disabled input cannot move it —
                        // but only one of them is the booking's own record, and a never-asked null must
                        // not read as a deliberate 0 through a seeding default.
                        value={viewOnly ? String(committedDelay ?? 0) : delayMinutes}
                        onChange={(e) => setDelayMinutes(e.target.value.replace(/\D/g, "").slice(0, 3))}
                        onBlur={() => delayMinutes === "" && setDelayMinutes("0")}
                      />
                      <Typography variant="paragraph" className="block text-neutral-silver-gray">
                        Free courtesy waiting time: 30 minutes for domestic flights and 60 minutes for international flights.
                      </Typography>
                    </div>
                    {/* Only when the card CANNOT state the pickup. `frozenArrival` is the arrival the booked
                      pickup was actually built from, so where it is set the card carries it — as the
                      `booked from 5:10 AM` line under the live landing — and that plus the delay row say the
                      pickup out loud: 5:10 AM + 50 min = the 6:00 AM booked. A time input beside that is a
                      redundant editable-looking control on a booking nobody can edit.

                      Where it is NULL the card has only the LIVE arrival, which the frozen pickup does not
                      reconcile against — a job closed before its flight landed is the ordinary way in, and
                      the flight goes on moving afterwards. Nothing on the card then adds up to the booked
                      pickup (measured: live 9:25 PM + 20 reads as 9:45 PM against a booked 6:00 PM), so the
                      booked time has to be on screen somewhere. `anchoredWorkingHolds` lists the other routes
                      here: a clamped pickup, an operator hand-edit, a delay changed after the last engine
                      write. */}
                    {viewOnly && !frozenArrival && manualTimeEntry}
                  </>
                ) : (
                  <>
                    {/* The client can always still book — manual time entry takes over. The reason differs:
                    lookup failed, the flight won't arrive as looked up, or it wasn't found. A cancelled or
                    diverted flight never reaches here; it is handled above, collapsed. */}
                    <Alert type={isFlightError ? "warning" : "info"} message={flightNotice} />
                    {manualTimeEntry}
                  </>
                )}
              </div>
            ) : (
              /* No flight number, or no flight-tracking add-on. View-only no longer lands here just for
               being view-only — it takes the branch above and gets the card — but a non-editable booking
               with tracking switched off still does, and shows the booked date and time from the hydrated
               draft with the wait published by the summary's own rows. */
              manualTimeEntry
            ))}
          {error && (
            <Typography variant="paragraph" className="mt-2 block text-danger">
              {error}
            </Typography>
          )}
        </div>
      </ViewOnlyFieldset>

      <FlightDetailsModal
        open={flightModalOpen}
        initialValue={pickupFlightNumber ?? ""}
        onConfirm={(flightNumber) => {
          confirmFlightNumber(flightNumber);
          setFlightModalOpen(false);
        }}
        onNotFlying={() => {
          notFlying();
          setFlightModalOpen(false);
        }}
        onDismiss={() => setFlightModalOpen(false)}
      />

      {!viewOnly && (
        <div className="flex flex-col gap-2 px-5 pb-5">
          {/* While the flight is resolving, the manual fields are hidden, so a Proceed would silently
              commit the unseen seed time — block it until the lookup settles.

              A DISRUPTION with nothing anchored is the same hazard permanently: this panel shows no time
              fields, but `resolvePickup`'s manual branch still reads the seeded hour/minute behind them, so
              Proceed would commit `defaultPickupSlot()` — the wall clock when the panel mounted — onto the
              selected date. Same-day it is worse than invisible: that seed rounds to the NEAREST five
              minutes, so it is routinely already past, and the client is handed "Pickup time can't be in
              the past" over a panel with no field to correct it.

              Anchored is exempt and must stay so: that branch has a committed pickup to hand back, so a
              client editing something else on a booking whose flight was cancelled is never trapped here.
              The unanchored case has its two exits in the modal above — a new flight, or Not Flying. */}
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={handleProceed}
            disabled={(!!pickupFlightNumber && (isFlightFetching || isFlightTrackingResolving)) || (isDisrupted && !isAnchored)}
          >
            {editedDriverArrival ? `Confirm new time: ${format(editedDriverArrival, "h:mm a")}` : isEdit ? "Confirm" : "Proceed"}
          </Button>
          {layout === "sheet" && (
            <Button variant="custom" size="md" className="text-info" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
